import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CheckCircle, XCircle, Search, RotateCcw, Filter, ArrowUpDown, Loader2, ExternalLink, Download, FileText, FileDown, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Calendar, Zap } from 'lucide-react';
import { IndexCheckResult } from '@/types/amazonIndexChecker';
import { amazonIndexCheckerService } from '@/services/amazonIndexCheckerService';
import { exportResults, ExportFormat } from '@/utils/exportUtils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface PreviousResultsProps {
  results: IndexCheckResult[];
  company: string;
  asin: string;
  userEmail: string;
  onRerunTerm: (term: string) => void;
  rerunningTerm?: string | null;
  isChecking?: boolean;
  checkingProgress?: { current: number; total: number };
}

type SortOption = 'term-asc' | 'term-desc' | 'date-asc' | 'date-desc';
type FilterOption = 'all' | 'indexed' | 'not-indexed' | 'status-changed' | 'newly-indexed' | 'newly-deindexed';

export const PreviousResults: React.FC<PreviousResultsProps> = ({
  results,
  company,
  asin,
  userEmail,
  onRerunTerm,
  rerunningTerm = null
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  
  // Page size options for user selection
  const pageSizeOptions = [25, 50, 100, 200];

  // Filter and sort results
  const filteredAndSortedResults = useMemo(() => {
    let filtered = results;

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(result => 
        result.term.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply index status filter
    if (filterBy !== 'all') {
      filtered = filtered.filter(result => {
        switch (filterBy) {
          case 'indexed':
            return result.isIndexed;
          case 'not-indexed':
            return !result.isIndexed;
          case 'status-changed':
            return result.statusChanged === true;
          case 'newly-indexed':
            return result.changeType === 'newly_indexed';
          case 'newly-deindexed':
            return result.changeType === 'newly_deindexed';
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'term-asc':
          return a.term.localeCompare(b.term);
        case 'term-desc':
          return b.term.localeCompare(a.term);
        case 'date-asc':
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        case 'date-desc':
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [results, searchTerm, sortBy, filterBy]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredAndSortedResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedResults = filteredAndSortedResults.slice(startIndex, endIndex);

  // Reset to first page when filters change
  const resetPage = () => setCurrentPage(1);

  // Handle filter changes and reset pagination
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    resetPage();
  };

  const handleFilterChange = (value: FilterOption) => {
    setFilterBy(value);
    resetPage();
  };

  const handleSortChange = (value: SortOption) => {
    setSortBy(value);
    resetPage();
  };

  const handlePageSizeChange = (value: string) => {
    setItemsPerPage(Number(value));
    resetPage();
  };

  const handleRerunSingle = async (term: string) => {
    try {
      await onRerunTerm(term);
    } catch (error) {
      console.error('Error rerunning term:', error);
    }
  };

  const handleExport = (format: ExportFormat) => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `amazon-index-results-${timestamp}.${format}`;
    exportResults(filteredAndSortedResults, format, filename);
  };

  const formatDateTime = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (results.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Results</span>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>{filteredAndSortedResults.length} of {results.length} terms</span>
          </div>
        </CardTitle>
        
        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search terms..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <Select value={filterBy} onValueChange={handleFilterChange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Results</SelectItem>
                <SelectItem value="indexed">Indexed Only</SelectItem>
                <SelectItem value="not-indexed">Not Indexed</SelectItem>
                <SelectItem value="status-changed">Status Changed</SelectItem>
                <SelectItem value="newly-indexed">Newly Indexed</SelectItem>
                <SelectItem value="newly-deindexed">Newly Deindexed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Sort */}
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-gray-500" />
            <Select value={sortBy} onValueChange={handleSortChange}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date-desc">Newest First</SelectItem>
                <SelectItem value="date-asc">Oldest First</SelectItem>
                <SelectItem value="term-asc">Term A-Z</SelectItem>
                <SelectItem value="term-desc">Term Z-A</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Page Size Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Show:</span>
            <Select value={itemsPerPage.toString()} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map(size => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Export */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg">
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileDown className="h-4 w-4 mr-2" />
                CSV File
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('txt')}>
                <FileText className="h-4 w-4 mr-2" />
                Text File
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('md')}>
                <FileDown className="h-4 w-4 mr-2" />
                Markdown
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-2">
          {paginatedResults.map((result, index) => (
            <div
              key={`${result.term}-${result.timestamp}`}
              className="flex items-center justify-between p-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <div className="flex items-center gap-3 flex-1">
                {result.isIndexed ? (
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                )}
                
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{result.term}</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={result.searchUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                            >
                              <ExternalLink className="h-3 w-3 text-blue-600 hover:text-blue-800" />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>View search results on Amazon</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Status Change Indicators */}
                      {result.statusChanged && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className={`text-xs flex items-center gap-1 animate-pulse ${
                                  result.changeType === 'newly_indexed'
                                    ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
                                    : 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800'
                                }`}
                              >
                                {result.changeType === 'newly_indexed' ? (
                                  <TrendingUp className="h-3 w-3" />
                                ) : (
                                  <TrendingDown className="h-3 w-3" />
                                )}
                                {result.changeType === 'newly_indexed' ? 'NEWLY INDEXED' : 'NEWLY DEINDEXED'}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                Status changed from {result.previousStatus ? 'indexed' : 'not indexed'} to {result.isIndexed ? 'indexed' : 'not indexed'}
                                {result.isQuarterlyCheck && ' (3-month check)'}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      {/* Quarterly Check Indicator */}
                      {result.isQuarterlyCheck && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="text-xs flex items-center gap-1 bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800"
                              >
                                <Calendar className="h-3 w-3" />
                                3-MONTH
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Result from quarterly scheduled check</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          result.isIndexed
                            ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                            : 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
                        }`}
                      >
                        {result.isIndexed ? 'Indexed' : 'Not Indexed'}
                      </Badge>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handleRerunSingle(result.term)}
                              disabled={rerunningTerm === result.term}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                            >
                              <RotateCcw
                                className={`h-3 w-3 text-gray-500 hover:text-gray-700 ${
                                  rerunningTerm === result.term ? 'animate-spin' : ''
                                }`}
                              />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{rerunningTerm === result.term ? 'Rechecking...' : 'Recheck this term'}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <span className="text-xs text-gray-500 ml-2">
                        {formatDateTime(result.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {paginatedResults.length === 0 && filteredAndSortedResults.length > 0 && (
            <div className="text-center py-8 text-gray-500">
              No results on this page
            </div>
          )}
          
          {filteredAndSortedResults.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No results match your search criteria
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <div className="text-sm text-gray-500">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedResults.length)} of {filteredAndSortedResults.length} results
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              
              <div className="flex items-center gap-1">
                {/* Show page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage > totalPages - 3) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className="w-8 h-8 p-0"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PreviousResults;